import type { UpdatePatientRequest } from "@louise/medical-history/basic-info/types";
import type { ExposureState } from "./ExposureContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

export function transformExposureStateToRequest(
  state: ExposureState,
): RequestBody<paths["/{tenantId}/patients/{patientId}"]["patch"]> {
  const exposuresPayload: NonNullable<
    NonNullable<UpdatePatientRequest["lifestyle"]>["exposures"]
  > = [];

  // Create set of IDs to delete for quick lookup (prevents double-sending)
  const idsToDeleteSet = new Set(state.exposureIdsToDelete || []);

  // Add deletions from exposureIdsToDelete
  state.exposureIdsToDelete.forEach((id) => {
    if (id) {
      exposuresPayload.push({ id, op: "delete" });
    }
  });

  // If user answered "No" to having exposures, delete all existing exposures
  // (those with IDs that haven't already been marked for deletion)
  if (state.hasExposures === false) {
    state.exposureDetails.forEach((detail) => {
      if (detail.id && !idsToDeleteSet.has(detail.id)) {
        exposuresPayload.push({ id: detail.id, op: "delete" });
      }
    });
    // Return early with delete operations only and hasExposures flag
    const lifestylePayload: NonNullable<UpdatePatientRequest["lifestyle"]> = {
      hasExposures: false,
    };
    if (state.lifestyleId) {
      lifestylePayload.id = state.lifestyleId;
    }
    if (exposuresPayload.length > 0) {
      lifestylePayload.exposures = exposuresPayload;
    }
    return {
      lifestyle: lifestylePayload,
    };
  }

  // Add exposures to keep (excluding those marked for deletion)
  state.exposureDetails
    .filter((detail) => !detail.id || !idsToDeleteSet.has(detail.id))
    .forEach((detail) => {
    const entry: NonNullable<(typeof exposuresPayload)[number]> = {};

    if (detail.id) entry.id = detail.id;
    if (detail.exposure != null) entry.exposure = detail.exposure;
    if (detail.frequency != null) entry.frequency = detail.frequency;
    if (detail.context != null && detail.context !== "") {
      entry.context = detail.context;
    }
    if (detail.other != null && detail.other !== "") {
      entry.other = detail.other;
    }

    exposuresPayload.push(entry);
  });

  const lifestylePayload: NonNullable<UpdatePatientRequest["lifestyle"]> = {};

  if (state.lifestyleId) {
    lifestylePayload.id = state.lifestyleId;
  }

  if (state.hasExposures !== undefined) {
    lifestylePayload.hasExposures = state.hasExposures ?? null;
  }

  if (exposuresPayload.length > 0) {
    lifestylePayload.exposures = exposuresPayload;
  }

  if (Object.keys(lifestylePayload).length === 0) {
    return {};
  }

  return {
    lifestyle: lifestylePayload,
  };
}
